import * as React from 'react'

import { ConnectError } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Text } from '@chakra-ui/react'

import {
  DangerButton,
  ModalView,
  PrimaryButton,
  TertiaryButton,
  toaster,
} from '@enechain/ecloud-designsystem'

import { useSuspendMember } from '~/pages/Members/hooks/useSuspendMember'
import { useUnsuspendMember } from '~/pages/Members/hooks/useUnsuspendMember'

export type SuspendMode = 'suspend' | 'unsuspend'

const CONFIG = {
  suspend: {
    titleKey: 'pages.members.action.suspend.title',
    descriptionKey: 'pages.members.action.suspend.description',
    confirmKey: 'pages.members.action.suspend.confirm',
    successKey: 'pages.members.action.suspend.success',
    ConfirmButton: DangerButton,
  },
  unsuspend: {
    titleKey: 'pages.members.action.unsuspend.title',
    descriptionKey: 'pages.members.action.unsuspend.description',
    confirmKey: 'pages.members.action.unsuspend.confirm',
    successKey: 'pages.members.action.unsuspend.success',
    ConfirmButton: PrimaryButton,
  },
} as const

type Props = {
  memberId: string
  mode: SuspendMode
  open: boolean
  onClose: () => void
  onSuccess: () => void
}
const SuspendModal: React.FC<Props> = ({
  memberId,
  mode,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const suspend = useSuspendMember(memberId, onSuccess)
  const unsuspend = useUnsuspendMember(memberId, onSuccess)

  const isPending = mode === 'suspend' ? suspend.isPending : unsuspend.isPending
  const reset = mode === 'suspend' ? suspend.reset : unsuspend.reset

  const { titleKey, descriptionKey, confirmKey, successKey, ConfirmButton } =
    CONFIG[mode]

  const onConfirm = (): void => {
    const options = {
      onSuccess: () => {
        toaster.create({
          title: t(successKey),
          type: 'success',
        })
      },
      onError: (error: ConnectError) => {
        toaster.create({
          title: error.message,
          type: 'error',
        })
      },
    }
    if (mode === 'suspend') {
      suspend.mutate({ memberId }, options)
    } else {
      unsuspend.mutate({ memberId }, options)
    }
  }

  return (
    <ModalView
      title={t(titleKey)}
      open={open}
      onClose={onClose}
      onExitComplete={reset}
      bodyChildren={<Text whiteSpace="pre-wrap">{t(descriptionKey)}</Text>}
      footerChildren={
        <>
          <TertiaryButton onClick={onClose}>
            {t('action.cancel')}
          </TertiaryButton>
          <ConfirmButton
            onClick={onConfirm}
            loading={isPending}
            disabled={isPending}
            spinnerPlacement="start"
          >
            {t(confirmKey)}
          </ConfirmButton>
        </>
      }
    />
  )
}

export default React.memo(SuspendModal)
